import { CdnAsset } from "#components/ui/CdnAsset.tsx";
import { CompactingIcon } from "#components/ui/CompactingIcon.tsx";
import { ComposingIcon } from "#components/ui/ComposingIcon.tsx";
import { OfficevibeIcon } from "#components/ui/OfficevibeIcon.tsx";
import { SearchingIcon } from "#components/ui/SearchingIcon.tsx";
import { SkillIcon } from "#components/ui/SkillIcon.tsx";
import { SourcesIcon } from "#components/ui/SourcesIcon.tsx";
import { ThinkingIcon } from "#components/ui/ThinkingIcon.tsx";
import { WorkleapIcon } from "#components/ui/WorkleapIcon.tsx";
import { WorkleapPerformanceIcon } from "#components/ui/WorkleapPerformanceIcon.tsx";
import { ThinkingStepCodes, ThinkingStepStates, type AssistantThinkingStep } from "#features/conversations/types/ThinkingStep.ts";
import { inlineSourceIcons } from "#features/conversations/types/channelGlyphs.tsx";
import { googleProductChannels } from "#features/conversations/types/channels.tsx";
import { getIntegrationManifest } from "#features/conversations/integrations/index.ts";
import { useThinkingStepDefaults } from "#features/conversations/hooks/useThinkingStepDefaults.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Disclosure, DisclosurePanel, Div, Flex, Spinner, Text, useColorSchemeValue, type ColorValue } from "@hopper-ui/components";
import { BuildIcon, MinusIcon, PlusIcon } from "@hopper-ui/icons";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGemoji from "remark-gemoji";
import "./ThinkingStepItem.css";

const sourceIconDimensions = { container: "1.25rem", asset: "0.625rem" };

const iconKeyMap: Record<string, ReactNode> = {
    officevibe: <OfficevibeIcon />,
    performance: <WorkleapPerformanceIcon />,
    platform: <WorkleapIcon />,
    contributions: <WorkleapIcon />,
    search: <SearchingIcon />
};

// Core sends one of these `iconKey`s on a Google search step, alongside `connectedIntegration: "google"`.
const googleProductIconKeys: Record<string, string> = {
    gmail: googleProductChannels.gmail.nameKey,
    google_drive: googleProductChannels.drive.nameKey,
    google_calendar: googleProductChannels.calendar.nameKey
};

interface ThinkingStepItemProps {
    step: AssistantThinkingStep;
    isMessageGenerating: boolean;
}

const MARKDOWN_CONFIG = {
    skipHtml: true,
    remarkPlugins: [remarkGemoji],
    allowedElements: ["p", "strong", "em", "code"],
    unwrapDisallowed: true,
    components: {
        p: ({ children }: { children?: ReactNode }) => <>{children}</>
    }
};

export const ThinkingStepItem = ({
    step,
    isMessageGenerating
}: ThinkingStepItemProps) => {
    // Hooks
    const { trackEvent } = useTracking();
    const { tShared } = useSharedTranslation();
    const { getDefaultStepTitle, getDefaultStepDetail } = useThinkingStepDefaults();
    const sourcesChannels = step.sourceChannels ?? [];

    // Color scheme
    const completedColor = useColorSchemeValue("neutral-weakest", "core_rock-500") as ColorValue;
    const activeColor = useColorSchemeValue("neutral", "core_samoyed") as ColorValue;
    const iconBorder = useColorSchemeValue("samoyed", "rock-900") as ColorValue;

    // State
    const [isExpanded, setIsExpanded] = useState(false);

    // Derived state
    const isCompleted = step.state === ThinkingStepStates.completed;
    const isActive = step.state === ThinkingStepStates.active && isMessageGenerating;
    const textColor = isActive ? activeColor : completedColor;
    const shouldShowSourceIcons = step.code === ThinkingStepCodes.sources && sourcesChannels.length > 0;

    // Derived values
    const stepDetail = step.detail || getDefaultStepDetail(step.code, step.sourceCount, step.sourceTitles, step.title);
    const stepLabel = step.title || getDefaultStepTitle(step.code, step.sourceCount);

    const stepIcon = useMemo(() => {
        const productIcon = step.iconKey ? inlineSourceIcons[googleProductIconKeys[step.iconKey]] : undefined;

        if (productIcon) {
            return productIcon;
        }

        const manifest = step.connectedIntegration ? getIntegrationManifest(step.connectedIntegration) : undefined;

        if (manifest) {
            const Glyph = manifest.glyph;

            return <Glyph />;
        }

        if (step.iconKey && iconKeyMap[step.iconKey]) {
            return iconKeyMap[step.iconKey];
        }

        switch (step.code) {
            case ThinkingStepCodes.thinking:
                return <ThinkingIcon />;
            case ThinkingStepCodes.searching:
                return <SearchingIcon />;
            case ThinkingStepCodes.toolRunning:
            case ThinkingStepCodes.processingPageAction:
                return <BuildIcon size="sm" />;
            case ThinkingStepCodes.assistantMessage:
                return <ComposingIcon />;
            case ThinkingStepCodes.compacting:
                return <CompactingIcon />;
            case ThinkingStepCodes.skillLoaded:
                return <SkillIcon />;
            case ThinkingStepCodes.sources:
                return <SourcesIcon />;
        }
    }, [step.code, step.iconKey, step.connectedIntegration]);

    const displayIcon = useMemo(() => {
        if (isCompleted || !isMessageGenerating) {
            return stepIcon;
        }

        return <Spinner size="sm" />;
    }, [isCompleted, isMessageGenerating, stepIcon]);

    // Callbacks
    const handleExpandedChange = useCallback((expanded: boolean) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Assistant Message",
            Trigger: expanded ? "Expand Thought Process Step" : "Collapse Thought Process Step"
        });

        setIsExpanded(expanded);
    }, [trackEvent]);

    // Render
    return (
        <Disclosure isExpanded={isExpanded} onExpandedChange={handleExpandedChange}>
            <Button
                slot="trigger"
                variant="ghost-secondary"
                justifyContent="flex-start"
                color={textColor}
                colorHover={activeColor}
                colorFocus={activeColor}
                className="wai-thinking-step-button"
                height="auto"
                UNSAFE_minHeight="2rem"
                maxWidth="100%"
                textAlign="left"
                position="relative"
                marginLeft="inline-xs"
            >
                <Flex
                    direction="row"
                    gap="inline-sm"
                    alignItems="center"
                >
                    <Div
                        width="core_160"
                        height="core_160"
                        className="wai-thinking-step-icon"
                    >
                        {displayIcon}
                    </Div>
                    <Text
                        fontWeight="core_400"
                        size="sm"
                        color="inherit"
                        style={{ textWrap: "wrap" }}
                    >
                        {stepLabel}
                    </Text>
                    {shouldShowSourceIcons && (
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            order={2}
                            UNSAFE_marginLeft="auto"
                        >
                            {sourcesChannels.map(({ nameKey, iconUrlAlt }, index) => {
                                const inlineIcon = inlineSourceIcons[nameKey];

                                return (
                                    <Flex
                                        key={nameKey}
                                        alignItems="center"
                                        justifyContent="center"
                                        UNSAFE_width={sourceIconDimensions.container}
                                        UNSAFE_height={sourceIconDimensions.container}
                                        UNSAFE_marginLeft={index > 0 ? "-0.25rem" : "0"}
                                        borderRadius="circle"
                                        backgroundColor={inlineIcon ? "transparent" : "core_toad-50"}
                                        UNSAFE_border={inlineIcon ? "0.125rem solid transparent" : `0.125rem solid var(--hop-${iconBorder})`}
                                        zIndex={sourcesChannels.length - index}
                                    >
                                        {inlineIcon ?? (
                                            <CdnAsset
                                                asset={iconUrlAlt}
                                                UNSAFE_width={sourceIconDimensions.asset}
                                                UNSAFE_height={sourceIconDimensions.asset}
                                                alt={tShared(nameKey)}
                                            />
                                        )}
                                    </Flex>
                                );
                            })}
                        </Flex>
                    )}
                </Flex>
                <Flex
                    position="absolute"
                    left="0"
                    top="0"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    width="core_240"
                    className="wai-thinking-step-item-expand-icon"
                >
                    {isExpanded ? (
                        <Flex
                            width="core_80"
                            height="core_80"
                            overflow="hidden"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <MinusIcon
                                size="sm"
                                transition="opacity 0.18s"
                            />
                        </Flex>
                    ) : (
                        <Flex
                            width="core_80"
                            height="core_80"
                            overflow="hidden"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <PlusIcon
                                size="sm"
                                transition="opacity 0.18s"
                            />
                        </Flex>
                    )}
                </Flex>
            </Button>
            <DisclosurePanel padding={0}>
                <Div
                    fontSize="body-xs"
                    color={textColor}
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    marginY="stack-sm"
                    UNSAFE_paddingLeft="2.125rem"
                >
                    <ReactMarkdown {...MARKDOWN_CONFIG}>
                        {stepDetail}
                    </ReactMarkdown>
                </Div>
            </DisclosurePanel>
        </Disclosure>
    );
};
